import type { ReactNode } from 'react';
import React from 'react';
import { asLegacyRef } from 'Src/types/widget-helpers';
import { classnames } from 'Utils/classnames/classnames.utils';
import { constants } from '@clubmed/utils';
import { ICONS } from 'Components/utils/icon/icons';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { Button } from 'Components/frameworks/button/Button';
import { BottomNavbarTotalPrice } from 'Components/widgets/bottom-navbar-layer/BottomNavbarTotalPrice';
import { PriceTicketFavorite } from 'Components/widgets/bottom-navbar-layer/PriceTicketFavorite';
import { SelectedRate } from 'Components/widgets/bottom-navbar-layer/SelectedRate';
import { asProposalPrice } from 'Src/types/cast-helpers';
import { getKeyValue } from 'Src/i18n';
import { INSTALMENT_LOCALES } from 'Components/frameworks/price/Price';
import { useAppContext } from 'Context/ReactAppContext';
const { FLEX_RATES } = constants;
type BottomNavbarButton = {
  ariaLabel?: string;
  'aria-label'?: string;
  dataTestId?: string;
  'data-testid'?: string;
  label: string | null | undefined;
};
type Props = {
  addedServicesNumber?: number;
  backButton?: BottomNavbarButton;
  children?: ReactNode;
  className?: string;
  closeButton?: BottomNavbarButton;
  detailsButton?: BottomNavbarButton;
  forwardRef?: ReactRef;
  hasCloseButton?: boolean;
  hasPrice?: boolean;
  hasSelectedRate?: boolean;
  isFavorite?: boolean;
  isNextStep?: boolean;
  isPriceLoading?: boolean;
  isOnLastPage?: boolean;
  navbarRef?: ReactRef;
  onClickBack?: () => void;
  onClickClose?: () => void;
  onClickDetails?: () => void;
  onClickFavorite?: () => void;
  price?: ProposalPriceDonation | CartPrice;
  priceSlot?: ReactNode;
};
export const BottomNavbar = ({
  addedServicesNumber = 0,
  backButton,
  children,
  className,
  isOnLastPage,
  closeButton,
  detailsButton,
  forwardRef,
  hasCloseButton = false,
  hasPrice = false,
  hasSelectedRate,
  isFavorite = false,
  isNextStep = false,
  isPriceLoading = false,
  navbarRef,
  onClickBack,
  onClickClose,
  onClickDetails,
  onClickFavorite,
  price,
  priceSlot,
}: Props) => {
  const { isTablet } = useResponsive();
  const { locale } = useAppContext();
  const isMobile = !isTablet;
  const displayInstalment =
    getKeyValue('common.price.displayInstalmentsPrice') && INSTALMENT_LOCALES.includes(locale);
  const hidePriceInfo = displayInstalment && isOnLastPage;
  return (
    <div
      className={classnames(
        'fixed inset-x-0 bottom-0 isolate w-full border-t-1 border-lightGrey bg-white lg:flex lg:justify-center',
        className,
      )}
      data-testid="BottomNavbar"
      ref={asLegacyRef<HTMLDivElement>(forwardRef)}
    >
      {onClickFavorite && (
        <PriceTicketFavorite
          isFilled={isFavorite}
          isNextStep={isNextStep}
          isSticky
          onClick={onClickFavorite}
        />
      )}

      <div
        className={classnames(
          'relative z-1 flex max-w-site flex-wrap items-center justify-between gap-4 px-20 py-5 sm:flex-nowrap md:gap-10 md:px-0 md:py-20 lg:w-full',
          {
            'h-[110px]': !hasSelectedRate || hidePriceInfo,
            'h-[160px]': hasSelectedRate && isMobile && !hidePriceInfo,
          },
        )}
        ref={asLegacyRef<HTMLDivElement>(navbarRef)}
      >
        <div className="hidden flex-1 lg:block">
          <Button
            {...backButton}
            className="font-bold"
            iconLeft={{
              hasRtl: true,
              name: ICONS.ARROW_LEFT,
            }}
            onClick={onClickBack}
            theme="link"
          >
            {backButton?.label}
          </Button>
        </div>
        {hidePriceInfo
          ? null
          : hasPrice && (
              <div className="relative w-1/2 shrink-0 md:w-auto" id="totalPriceDescription">
                <BottomNavbarTotalPrice
                  isLoading={isPriceLoading}
                  price={price}
                  hideUnit={Boolean(priceSlot)}
                  displayInstalment={displayInstalment}
                  isOnLastPage={isOnLastPage}
                  className="text-normal items-start md:items-center md:text-center"
                />
                <div className={`${isPriceLoading ? 'hidden' : 'block'}`}>{priceSlot}</div>
                {Boolean(addedServicesNumber) && (
                  <span className="absolute bottom-full end-0 z-1 inline-flex aspect-square size-30 transform items-center justify-center rounded-full bg-success p-5 text-14 font-bold text-white sm:transform-none ltr:translate-x-1/2 rtl:-translate-x-1/2">
                    {addedServicesNumber > 0 ? `+${addedServicesNumber}` : addedServicesNumber}
                  </span>
                )}
                {hasSelectedRate && (
                  <div
                    className={classnames(
                      'start-full top-[20px] mt-5 w-fit rounded-pill px-10 py-5 sm:absolute sm:mt-0 md:mx-10',
                      {
                        'bg-green': [FLEX_RATES.STANDARD, FLEX_RATES.EBB].includes(
                          price?.selectedRate,
                        ),
                        'bg-sienna': price?.selectedRate !== FLEX_RATES.STANDARD,
                      },
                    )}
                  >
                    <SelectedRate price={asProposalPrice(price)} />
                  </div>
                )}
              </div>
            )}

        {!isTablet && children}

        <div className="flex w-full flex-none items-center gap-10 sm:w-auto sm:flex-1 lg:justify-end">
          <Button
            {...detailsButton}
            className="font-bold"
            iconRight={{
              name: ICONS.ARROW_DOWN,
            }}
            onClick={onClickDetails}
            theme="link"
          >
            {detailsButton?.label}
          </Button>

          {hasCloseButton && (
            <Button
              className="font-bold"
              data-testid={closeButton?.dataTestId}
              onClick={onClickClose}
              theme="link"
            >
              {closeButton?.label}
            </Button>
          )}
          {isTablet && children}
        </div>
      </div>
    </div>
  );
};
